import { Clock, List, Plus, SlidersHorizontal } from "lucide-react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from "@hugeicons/core-free-icons";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { CalendarView } from "./types";

interface AppointmentsWeekToolbarProps {
  canManageAppointments: boolean;
  currentPeriodLabel: string;
  currentView: CalendarView;
  isFilterPanelOpen: boolean;
  onToday: () => void;
  onViewChange: (view: CalendarView) => void;
  onPreviousPeriod: () => void;
  onNextPeriod: () => void;
  onToggleFilters: () => void;
  onBlockTime: () => void;
  onAddAppointment: () => void;
  onOpenAppointmentList: () => void;
  showAppointmentList?: boolean;
}

export const AppointmentsWeekToolbar = ({
  canManageAppointments,
  currentPeriodLabel,
  currentView,
  isFilterPanelOpen,
  onToday,
  onViewChange,
  onPreviousPeriod,
  onNextPeriod,
  onToggleFilters,
  onBlockTime,
  onAddAppointment,
  onOpenAppointmentList,
  showAppointmentList = false,
}: AppointmentsWeekToolbarProps) => {
  const currentViewLabel =
    currentView === "week" ? "Weekly View" : "Daily View";

  return (
    <div className="shrink-0 border-b border-border p-4 sm:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onToday}>
            Today
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-lg bg-accent"
            onClick={onPreviousPeriod}
            aria-label={`Previous ${currentView === "week" ? "week" : "day"}`}
          >
            <HugeiconsIcon icon={ChevronLeft} size={16} />
          </Button>
          <p className="z-h2 min-w-0 font-semibold text-foreground">
            {currentPeriodLabel}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-lg bg-accent"
            onClick={onNextPeriod}
            aria-label={`Next ${currentView === "week" ? "week" : "day"}`}
          >
            <HugeiconsIcon icon={ChevronRight} size={16} />
          </Button>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="min-w-[7.5rem] justify-between gap-3 font-normal"
              >
                {currentViewLabel}
                <HugeiconsIcon
                  icon={ChevronDown}
                  size={16}
                  className="opacity-80"
                />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuRadioGroup
                value={currentView}
                onValueChange={(value) => onViewChange(value as CalendarView)}
              >
                <DropdownMenuRadioItem value="week" className="cursor-pointer">
                  Weekly View
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="day" className="cursor-pointer">
                  Daily View
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          {showAppointmentList ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-2 font-normal"
              onClick={onOpenAppointmentList}
            >
              <List className="h-4 w-4" />
              Appointment List
            </Button>
          ) : null}

          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(
              "gap-2 font-normal",
              isFilterPanelOpen && "border-primary bg-primary/10 text-primary"
            )}
            onClick={onToggleFilters}
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filter
          </Button>

          {canManageAppointments ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" size="sm" className="gap-2 px-3.5">
                  <Plus className="h-4 w-4" />
                  Create New
                  <HugeiconsIcon
                    icon={ChevronDown}
                    size={16}
                    className="opacity-80"
                  />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  className="cursor-pointer gap-2"
                  onClick={onBlockTime}
                >
                  <Clock className="h-4 w-4" />
                  Block Time
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer gap-2"
                  onClick={onAddAppointment}
                >
                  <Plus className="h-4 w-4" />
                  Appointment
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>
    </div>
  );
};
